import { Container, Row } from "react-bootstrap";
import {
  getMarketplaceOrderInsights,
  getRevenueInsights,
  getProductInsights,
  getCategoryInsights,
  getSupportOrderInsights,
  getShopFulfillmentInsights,
  getOrdersPerDay,
  getOrdersByPaymentMethod,
  getOrdersByHour
} from "../api/getapi";
import { useState, useEffect, useCallback } from "react";
import OrderAnalyticsSection from "../Components/DashboardAnalytics/OrderAnalyticsSection";
import ROASNetSection from "../Components/DashboardAnalytics/ROASNetSection";
import moment from "moment";

const MarketplaceDashboard = () => {
  const [marketplaceOrderInsights, setMarketplaceOrderInsights] = useState({ general: {}, perShop: [] });
  const [revenueInsights, setRevenueInsights] = useState({ general: {}, perShop: [] });
  const [productInsights, setProductInsights] = useState([]);
  const [categoryInsights, setCategoryInsights] = useState({ overall: [], byStatus: [] });
  const [supportOrderInsights, setSupportOrderInsights] = useState({ general: {}, byUser: [] });
  const [shopFulfillmentInsights, setShopFulfillmentInsights] = useState([]);
  const [ordersPerDay, setOrdersPerDay] = useState([]);
  const [ordersByPaymentMethod, setOrdersByPaymentMethod] = useState([]);
  const [ordersByHour, setOrdersByHour] = useState([]);
  const [loading, setLoading] = useState(true);
  const [timePeriod, setTimePeriod] = useState("all-time");
  const [customDateFrom, setCustomDateFrom] = useState(moment().subtract(29, "days").format("YYYY-MM-DD"));
  const [customDateTo, setCustomDateTo] = useState(moment().format("YYYY-MM-DD"));
  const [shopFilter, setShopFilter] = useState(null);
  const [statusFilter, setStatusFilter] = useState("");
  const [ordersRange, setOrdersRange] = useState("last-30-days");
  const [ordersDateFrom, setOrdersDateFrom] = useState(moment().subtract(29, "days").format("YYYY-MM-DD"));
  const [ordersDateTo, setOrdersDateTo] = useState(moment().format("YYYY-MM-DD"));

  const fetchOrderAnalytics = useCallback(async (period = timePeriod) => {
    setLoading(true);
    try {
      const shopId = shopFilter?.value?.id || null;
      const customDate = period === "custom" && customDateFrom && customDateTo
        ? { from: customDateFrom, to: customDateTo }
        : undefined;
      const [marketplace, revenue, products, categories, support, fulfillment, orders, byPaymentMethod, byHour] = await Promise.all([
        getMarketplaceOrderInsights({ timePeriod: period, shop: shopId, status: statusFilter, date: customDate }),
        getRevenueInsights({ timePeriod: period, shop: shopId, status: statusFilter, date: customDate }),
        getProductInsights({ timePeriod: period, shop: shopId, status: statusFilter, limit: 10, date: customDate }),
        getCategoryInsights({ timePeriod: period, shop: shopId, status: statusFilter, date: customDate }),
        getSupportOrderInsights({ timePeriod: period, date: customDate }),
        getShopFulfillmentInsights({ timePeriod: period, shop: shopId, date: customDate }),
        getOrdersPerDay({ shop: shopId, status: statusFilter, dateFrom: ordersDateFrom, dateTo: ordersDateTo }),
        getOrdersByPaymentMethod({ timePeriod: period, shop: shopId, status: statusFilter, date: customDate }),
        getOrdersByHour({ timePeriod: period, shop: shopId, status: statusFilter, date: customDate })
      ]);
      setMarketplaceOrderInsights(marketplace.data);
      setRevenueInsights(revenue.data);
      setProductInsights(products.data);
      setCategoryInsights(categories.data);
      setSupportOrderInsights(support.data);
      setShopFulfillmentInsights(fulfillment.data);
      setOrdersPerDay(orders.data);
      setOrdersByPaymentMethod(byPaymentMethod.data);
      setOrdersByHour(byHour.data);
    } catch {} finally {
      setLoading(false);
    }
  }, [timePeriod, shopFilter, statusFilter, ordersDateFrom, ordersDateTo, customDateFrom, customDateTo]);

  useEffect(() => {
    fetchOrderAnalytics(timePeriod);
  }, [fetchOrderAnalytics, timePeriod]);

  return (
    <Container fluid className="px-xl-5">
      <Row className="py-2 g-2">
        <OrderAnalyticsSection
          marketplaceOrderInsights={marketplaceOrderInsights}
          revenueInsights={revenueInsights}
          productInsights={productInsights}
          categoryInsights={categoryInsights}
          supportOrderInsights={supportOrderInsights}
          shopFulfillmentInsights={shopFulfillmentInsights}
          ordersPerDay={ordersPerDay}
          ordersByPaymentMethod={ordersByPaymentMethod}
          ordersByHour={ordersByHour}
          timePeriod={timePeriod}
          onTimePeriodChange={setTimePeriod}
          customDateFrom={customDateFrom}
          onCustomDateFromChange={setCustomDateFrom}
          customDateTo={customDateTo}
          onCustomDateToChange={setCustomDateTo}
          shop={shopFilter}
          onShopChange={setShopFilter}
          status={statusFilter}
          onStatusChange={setStatusFilter}
          ordersRange={ordersRange}
          onOrdersRangeChange={(range) => {
            setOrdersRange(range);
            if (range === "custom") return;
            const now = moment();
            switch (range) {
              case "last-7-days":
                setOrdersDateFrom(now.clone().subtract(6, "days").format("YYYY-MM-DD"));
                break;
              case "last-30-days":
                setOrdersDateFrom(now.clone().subtract(29, "days").format("YYYY-MM-DD"));
                break;
              case "last-90-days":
                setOrdersDateFrom(now.clone().subtract(89, "days").format("YYYY-MM-DD"));
                break;
              case "this-month":
                setOrdersDateFrom(now.clone().startOf("month").format("YYYY-MM-DD"));
                break;
              case "this-year":
                setOrdersDateFrom(now.clone().startOf("year").format("YYYY-MM-DD"));
                break;
              default:
                setOrdersDateFrom("");
            }
            setOrdersDateTo(now.format("YYYY-MM-DD"));
          }}
          ordersDateFrom={ordersDateFrom}
          onOrdersDateFromChange={setOrdersDateFrom}
          ordersDateTo={ordersDateTo}
          onOrdersDateToChange={setOrdersDateTo}
          onRefresh={() => fetchOrderAnalytics(timePeriod)}
          loading={loading}
        />
      </Row>
      <Row className="py-2 g-2">
        <ROASNetSection />
      </Row>
    </Container>
  );
};

export default MarketplaceDashboard;
